import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useState } from 'react'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Badge } from '@/common/ui/badge'
import { Button } from '@/common/ui/button'
import { Switch } from '@/common/ui/switch'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import { Skeleton } from '@/common/ui/skeleton'
import { Edit, Plus, Trash2 } from 'lucide-react'
import { AlertModal } from '@/common/components/AlertModal'
import useQueryGetAllIntervalMessages from '../../api/hooks/useQueryGetAllIntervalMessages'
import useMutationUpdateIntervalMessage from '../../api/hooks/useMutationUpdateIntervalMessage'
import useMutationDeleteIntervalMessage from '../../api/hooks/useMutationDeleteIntervalMessage'
import { getConditionLabel, getChannelLabel } from '../../utils/conditionLabels'
import { formatIntervalSchedule } from '../../utils/intervalHelpers'
import { MessageChannel, LineTransactionType } from '../../types'

import type { IntervalMessageTransactionModel } from '../../types'

const ManageIntervalMessagesPage = () => {
	const navigate = useNavigate()
	const {
		data: intervalMessagesData,
		isLoading,
		refetch,
	} = useQueryGetAllIntervalMessages()

	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
		null,
	)

	const updateIntervalMessageMutation = useMutationUpdateIntervalMessage({
		onSuccess: () => {
			toast.success('อัปเดตสถานะสำเร็จ')
			refetch()
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ')
		},
	})

	const deleteIntervalMessageMutation = useMutationDeleteIntervalMessage({
		onSuccess: () => {
			toast.success('ลบข้อความอัตโนมัติตามช่วงเวลาสำเร็จ')
			setDeleteModalOpen(false)
			setSelectedMessageId(null)
			refetch()
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการลบข้อความอัตโนมัติตามช่วงเวลา')
		},
	})

	const handleToggleActive = (id: string, currentStatus: boolean) => {
		updateIntervalMessageMutation.mutate({
			id,
			data: {
				isActive: !currentStatus,
			},
		})
	}

	const handleDelete = () => {
		if (!selectedMessageId) return
		deleteIntervalMessageMutation.mutate(selectedMessageId)
	}

	const getChannelBadges = (message: IntervalMessageTransactionModel) => {
		const channels: JSX.Element[] = []

		if (message.intervalSmsTransaction) {
			channels.push(
				<Badge key="sms" variant="default">
					{getChannelLabel(MessageChannel.SMS)}
				</Badge>,
			)
		}

		if (message.intervalLineTransaction) {
			// Check if it's a flex hot game or regular LINE message
			if (message.lineTransactionType === LineTransactionType.FLEX_HOT_GAME) {
				channels.push(
					<Badge
						key="line-hot-game"
						variant="default"
						className="bg-purple-600"
					>
						{getChannelLabel(MessageChannel.LINE_HOT_GAME)}
					</Badge>,
				)
			} else {
				channels.push(
					<Badge key="line" variant="default" className="bg-green-600">
						{getChannelLabel(MessageChannel.LINE)}
					</Badge>,
				)
			}
		}

		return channels.length > 0 ? (
			channels
		) : (
			<span className="text-muted-foreground">-</span>
		)
	}

	const intervalMessages = intervalMessagesData?.data || []

	return (
		<PageContainer>
			<div className="space-y-4 px-4 sm:px-0">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
					<Heading
						title="จัดการข้อความอัตโนมัติตามช่วงเวลา"
						description="รายการข้อความที่ส่งอัตโนมัติตามช่วงเวลาที่กำหนด"
					/>
					<Button
						onClick={() => navigate('/operator/messaging/interval/create')}
					>
						<Plus className="mr-2 h-4 w-4" />
						สร้างข้อความอัตโนมัติตามช่วงเวลา
					</Button>
				</div>

				<Separator className="my-4 sm:my-6" />

				{isLoading ? (
					<div className="space-y-3">
						{[...Array(5)].map((_, i) => (
							<Skeleton key={i} className="h-16 w-full" />
						))}
					</div>
				) : intervalMessages.length === 0 ? (
					<div className="rounded-lg border p-8 text-center">
						<p className="text-muted-foreground">
							ยังไม่มีข้อความอัตโนมัติตามช่วงเวลา
						</p>
						<Button
							className="mt-4"
							onClick={() => navigate('/operator/messaging/interval/create')}
						>
							<Plus className="mr-2 h-4 w-4" />
							สร้างข้อความอัตโนมัติตามช่วงเวลา
						</Button>
					</div>
				) : (
					<div className="rounded-md border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>ข้อความ</TableHead>
									<TableHead>ช่วงเวลาส่ง</TableHead>
									<TableHead>เงื่อนไขผู้รับ</TableHead>
									<TableHead>ช่องทาง</TableHead>
									<TableHead>ส่งครั้งล่าสุด</TableHead>
									<TableHead>สถานะ</TableHead>
									<TableHead className="text-right">จัดการ</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{intervalMessages.map((message) => (
									<TableRow key={message.id}>
										<TableCell className="max-w-md">
											<div className="truncate font-medium">
												{message.message.substring(0, 60)}
												{message.message.length > 60 && '...'}
											</div>
										</TableCell>
										<TableCell>
											<div className="text-sm">
												{formatIntervalSchedule(
													message.intervalCondition,
													message.intervalValue,
													message.sendTime,
												)}
											</div>
										</TableCell>
										<TableCell>
											<div className="text-sm">
												{getConditionLabel(message.messageGroupCondition)}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex gap-1">
												{getChannelBadges(message)}
											</div>
										</TableCell>
										<TableCell>
											{message.lastSentDate ? (
												<div className="text-sm">
													{format(
														new Date(message.lastSentDate),
														'dd MMM yyyy HH:mm',
														{ locale: th },
													)}
												</div>
											) : (
												<span className="text-sm text-muted-foreground">
													ยังไม่เคยส่ง
												</span>
											)}
										</TableCell>
										<TableCell>
											<Switch
												checked={message.isActive}
												onCheckedChange={() =>
													handleToggleActive(message.id, message.isActive)
												}
												disabled={updateIntervalMessageMutation.isPending}
											/>
										</TableCell>
										<TableCell className="text-right">
											<div className="flex justify-end gap-2">
												<Button
													variant="ghost"
													size="icon"
													onClick={() =>
														navigate(
															`/operator/messaging/interval/${message.id}`,
														)
													}
												>
													<Edit className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														setSelectedMessageId(message.id)
														setDeleteModalOpen(true)
													}}
												>
													<Trash2 className="h-4 w-4 text-red-600" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</div>

			<AlertModal
				isOpen={deleteModalOpen}
				onClose={() => {
					setDeleteModalOpen(false)
					setSelectedMessageId(null)
				}}
				onConfirm={handleDelete}
				loading={deleteIntervalMessageMutation.isPending}
			/>
		</PageContainer>
	)
}

const GuardedManageIntervalMessagesPage = withUserPermission(
	ManageIntervalMessagesPage,
	{
		permissionType: PermissionType.Read,
		permissionKey: [PermissionKeys.MANAGE_SMS],
	},
)

export { GuardedManageIntervalMessagesPage as ManageIntervalMessagesPage }
